/** XeAgents.ai**/
import { useMemo, useState } from "react";
import { useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { INDUSTRIES, OTHER_INDUSTRY } from "@/components/template-customize/types";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Layers, Copy, Sparkles, LayoutTemplate, Wand2, Plus, AlertCircle, RefreshCw,
  Search, MessageSquare, Target, PhoneCall, HeartHandshake, Receipt, CalendarClock,
  ClipboardList, Headphones, Shapes,
} from "lucide-react";
import { useTranslation } from "react-i18next";

interface FlowTemplate {
  id: string;
  name: string;
  description: string;
  isTemplate: boolean;
  nodeCount: number;
  category?: string;
  /** The industries this template suits (server-side tags); the page filters on these. */
  industries?: string[];
  /** Inbound templates are for calls people place to us; this page offers outbound ones only. */
  direction?: "inbound" | "outbound";
  /** The agent's first spoken line - what someone is really choosing between. */
  openingLine?: string;
  scriptPreview?: Array<{ label: string; message: string }>;
  outcomes?: string[];
  preview: string[];
  requiredVariables?: Array<{ key: string; description: string; example: string }>;
}

const HEALTHCARE_INDUSTRY = "Healthcare & Clinics";
const ALL_INDUSTRIES = "All industries";
/** Healthcare first, then everything, then every other industry the customize page offers. */
const INDUSTRY_OPTIONS: string[] = [HEALTHCARE_INDUSTRY, ALL_INDUSTRIES, ...INDUSTRIES.filter((i) => i !== HEALTHCARE_INDUSTRY && i !== OTHER_INDUSTRY)];

/** One icon and accent per category, so the eye can tell a survey from a collections call. */
const CATEGORY_STYLE: Record<string, { icon: typeof Target; accent: string; tint: string }> = {
  Sales: { icon: Target, accent: "text-fuchsia-600 dark:text-fuchsia-400", tint: "bg-fuchsia-500/10" },
  Retention: { icon: HeartHandshake, accent: "text-emerald-600 dark:text-emerald-400", tint: "bg-emerald-500/10" },
  Collections: { icon: Receipt, accent: "text-amber-600 dark:text-amber-400", tint: "bg-amber-500/10" },
  Scheduling: { icon: CalendarClock, accent: "text-sky-600 dark:text-sky-400", tint: "bg-sky-500/10" },
  Research: { icon: ClipboardList, accent: "text-violet-600 dark:text-violet-400", tint: "bg-violet-500/10" },
  Support: { icon: Headphones, accent: "text-teal-600 dark:text-teal-400", tint: "bg-teal-500/10" },
  Other: { icon: Shapes, accent: "text-muted-foreground", tint: "bg-muted" },
};

/** Placeholders read as noise in a preview; show the shape of the sentence instead. */
function readable(text: string): string {
  return text.replace(/\{\{(\w+)\}\}/g, (_, key: string) => `[${String(key).replace(/_/g, " ")}]`);
}

export default function FlowTemplatesPage() {
  const { t } = useTranslation();
  const [, setLocation] = useLocation();
  const [search, setSearch] = useState("");
  // The landing page sector cards link here with ?industry=.
  const [industry, setIndustry] = useState<string>(() => {
    const wanted = new URLSearchParams(window.location.search).get("industry");
    return wanted && INDUSTRY_OPTIONS.includes(wanted) ? wanted : HEALTHCARE_INDUSTRY;
  });
  const [previewing, setPreviewing] = useState<FlowTemplate | null>(null);

  const { data: allTemplates = [], isLoading, isError, refetch } = useQuery<FlowTemplate[]>({
    queryKey: ["/api/flow-automation/flow-templates"],
  });

  const templates = useMemo(() => allTemplates.filter((tpl) => tpl.direction !== "inbound"), [allTemplates]);

  const categories = useMemo(
    () => ["All", ...Array.from(new Set(templates.map(tpl => tpl.category || "Other")))],
    [templates]
  );

  // Search covers the opening line too: people look for a template by how it starts
  // ("could I speak with") as readily as by its name.
  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return templates.filter(tpl => {
      if (industry !== ALL_INDUSTRIES && !(tpl.industries || []).includes(industry)) return false;
      if (!needle) return true;
      return [tpl.name, tpl.description, tpl.openingLine, ...(tpl.outcomes || [])]
        .some(field => String(field || "").toLowerCase().includes(needle));
    });
  }, [templates, search, industry]);

  // The customize page opens with the chosen industry already picked.
  const industryQuery = industry === ALL_INDUSTRIES ? "" : `?industry=${encodeURIComponent(industry)}`;

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-muted-foreground">{t("flowTemplates.loadingTemplates")}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-fuchsia-50 via-purple-100/50 to-pink-50 dark:from-fuchsia-950/40 dark:via-purple-900/30 dark:to-pink-950/40 border border-fuchsia-100 dark:border-fuchsia-900/50 p-6 md:p-8">
        <div className="absolute inset-0 bg-grid-slate-200/50 dark:bg-grid-slate-700/20 [mask-image:linear-gradient(0deg,transparent,rgba(255,255,255,0.5))]" />
        <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-fuchsia-500 to-purple-600 flex items-center justify-center shadow-lg shadow-fuchsia-500/25">
              <LayoutTemplate className="h-7 w-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground" data-testid="text-page-title">
                {t("flowTemplates.title")}
              </h1>
              <p className="text-muted-foreground mt-0.5">{t("flowTemplates.subtitle")}</p>
            </div>
          </div>
        </div>

        {/* Both tiles carry a number. The second used to read "Ready to use" - a label
            styled as a statistic, which tells the reader nothing they did not know. */}
        <div className="relative mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="bg-white/80 dark:bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-fuchsia-100/50 dark:border-fuchsia-800/30">
            <div className="flex items-center gap-2">
              <LayoutTemplate className="h-4 w-4 text-fuchsia-600 dark:text-fuchsia-400" />
              <div className="text-2xl font-bold text-fuchsia-700 dark:text-fuchsia-300">{templates.length}</div>
            </div>
            <div className="text-fuchsia-600/70 dark:text-fuchsia-400/70 text-sm">{t("flowTemplates.availableTemplates")}</div>
          </div>
          <div className="bg-white/80 dark:bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-purple-100/50 dark:border-purple-800/30">
            <div className="flex items-center gap-2">
              <Shapes className="h-4 w-4 text-purple-600 dark:text-purple-400" />
              <div className="text-2xl font-bold text-purple-700 dark:text-purple-300">{Math.max(categories.length - 1, 0)}</div>
            </div>
            <div className="text-purple-600/70 dark:text-purple-400/70 text-sm">
              {t("flowTemplates.categoriesLabel", "Categories, from sales to collections")}
            </div>
          </div>
        </div>
      </div>

      {/* Find one of sixteen without reading all sixteen. */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative w-full sm:max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("flowTemplates.searchPlaceholder", "Search templates...")}
            className="pl-9"
            data-testid="input-template-search"
          />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground whitespace-nowrap">{t("flowTemplates.industryLabel", "Industry")}</span>
          <Select value={industry} onValueChange={setIndustry}>
            <SelectTrigger className="w-full sm:w-64" data-testid="select-template-industry">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {INDUSTRY_OPTIONS.map((name) => (
                <SelectItem key={name} value={name}>
                  {name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {/* Custom Template Card - always first, and never filtered away */}
        {!search.trim() && (
          <Card className="hover-elevate border-2 border-dashed border-primary/30 bg-gradient-to-br from-primary/5 to-purple-500/5 h-full flex flex-col">
            <CardHeader>
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-primary to-purple-600 flex items-center justify-center">
                    <Wand2 className="h-4 w-4 text-white" />
                  </div>
                  <Badge className="bg-gradient-to-r from-primary to-purple-600 text-white border-0 text-xs">AI-Powered</Badge>
                </div>
                <Sparkles className="h-5 w-5 text-primary" />
              </div>
              <CardTitle className="text-xl">Custom Template</CardTitle>
              <CardDescription className="text-sm mt-2">
                Create a completely custom flow tailored to your business. Upload your company details and let AI generate the perfect conversation flow.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 flex flex-col flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant="secondary" className="text-xs">AI Generated</Badge>
                <Badge variant="outline" className="text-xs">Fully Customizable</Badge>
              </div>
              {/* mt-auto pins the action to the card floor, so it lines up with the Use
                  buttons on the template cards however much copy sits above it. */}
              <div className="mt-auto pt-2">
                <Button
                  className="w-full bg-gradient-to-r from-primary to-purple-600 hover:from-primary/90 hover:to-purple-600/90"
                  onClick={() => setLocation("/app/flows/templates/custom/customize")}
                  data-testid="button-use-custom-template"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create Custom Flow
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Load failure - without this the grid silently renders as "no templates" */}
        {isError && (
          <Card className="border-destructive/40 bg-destructive/5 h-full flex flex-col">
            <CardHeader>
              <div className="flex items-center gap-2 mb-3">
                <AlertCircle className="h-5 w-5 text-destructive" />
                <Badge variant="destructive" className="text-xs">Error</Badge>
              </div>
              <CardTitle className="text-xl">{t("flowTemplates.loadFailed")}</CardTitle>
              <CardDescription className="text-sm mt-2">{t("flowTemplates.loadFailedDesc")}</CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col flex-1">
              <Button variant="outline" className="w-full mt-auto" onClick={() => refetch()} data-testid="button-retry-templates">
                <RefreshCw className="h-4 w-4 mr-2" />
                {t("flowTemplates.retry")}
              </Button>
            </CardContent>
          </Card>
        )}

        {visible.map((template) => {
          const style = CATEGORY_STYLE[template.category || "Other"] || CATEGORY_STYLE.Other;
          const Icon = style.icon;
          const needs = (template.requiredVariables || []).map(v => v.key.replace(/_/g, " "));

          return (
            <Card key={template.id} className="hover-elevate h-full flex flex-col">
              <CardHeader>
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className={`h-8 w-8 rounded-lg flex items-center justify-center ${style.tint}`}>
                      <Icon className={`h-4 w-4 ${style.accent}`} />
                    </div>
                    <Badge variant="outline" className="text-xs">{template.category || "Other"}</Badge>
                  </div>
                  {/* Node count measures complexity, not value - a quiet aside rather than a
                      headline badge reading "36 nodes", which makes the best template look
                      like the most daunting one. */}
                  <span className="flex items-center gap-1 text-xs text-muted-foreground" title={`${template.nodeCount} steps`}>
                    <Layers className="h-3.5 w-3.5" />
                    {template.nodeCount}
                  </span>
                </div>
                <CardTitle className="text-xl" data-testid={`text-template-name-${template.id}`}>{template.name}</CardTitle>
                {/* Descriptions run from two lines to eight, which is what made the grid
                    look ragged. Clamped here; the full text is on the customize page. */}
                <CardDescription className="text-sm mt-2 line-clamp-3" title={template.description}>
                  {template.description}
                </CardDescription>
              </CardHeader>

              <CardContent className="space-y-4 flex flex-col flex-1">
                {/* What the agent actually opens with. This replaced three badges reading
                    "start, message, multiCondition" - identical on all sixteen cards. */}
                {template.openingLine && (
                  <blockquote className="border-l-2 border-muted-foreground/30 pl-3 text-sm text-muted-foreground italic line-clamp-3">
                    {`"${readable(template.openingLine)}"`}
                  </blockquote>
                )}

                {!!template.outcomes?.length && (
                  <div className="flex flex-wrap gap-1.5">
                    {template.outcomes.map((outcome) => (
                      <Badge key={outcome} variant="secondary" className="text-xs font-normal">{outcome}</Badge>
                    ))}
                  </div>
                )}

                {/* Surfaced before the click, not after: these are the values you must supply
                    for the script to speak about your business instead of "our company". */}
                {needs.length > 0 && (
                  <p className="text-xs text-muted-foreground">
                    <span className="font-medium">{t("flowTemplates.youWillNeed", "You will need")}: </span>
                    {needs.slice(0, 4).join(", ")}
                    {needs.length > 4 && ` +${needs.length - 4} more`}
                  </p>
                )}

                <div className="flex gap-2 mt-auto pt-2">
                  {!!template.scriptPreview?.length && (
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={() => setPreviewing(template)}
                      data-testid={`button-preview-template-${template.id}`}
                    >
                      <MessageSquare className="h-4 w-4 mr-2" />
                      {t("flowTemplates.preview", "Preview")}
                    </Button>
                  )}
                  <Button
                    className="flex-1"
                    onClick={() => setLocation(`/app/flows/templates/${template.id}/customize${industryQuery}`)}
                    data-testid={`button-use-template-${template.id}`}
                  >
                    <Copy className="h-4 w-4 mr-2" />
                    {t("flowTemplates.use", "Use")}
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Nothing matched - say so, rather than showing an empty page. */}
      {!isError && visible.length === 0 && (
        <div className="text-center py-12">
          <PhoneCall className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
          <p className="text-sm font-medium">{t("flowTemplates.noMatches", "No templates match that search")}</p>
          {/* "link" is not one of this Button's variants (see components/ui/button.tsx),
              so it silently rendered with no variant class at all. Reachable far more
              often now that the category filter has categories to filter by. */}
          <Button variant="ghost" onClick={() => { setSearch(""); setIndustry(ALL_INDUSTRIES); }}>
            {t("flowTemplates.clearFilters", "Clear search and filters")}
          </Button>
        </div>
      )}

      {/* See the call before committing to it. */}
      <Dialog open={!!previewing} onOpenChange={(open) => !open && setPreviewing(null)}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{previewing?.name}</DialogTitle>
            <DialogDescription>
              {t("flowTemplates.previewDescription", "The first few things the agent says. Values in brackets are filled in from your details.")}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {previewing?.scriptPreview?.map((step, i) => (
              <div key={i} className="rounded-lg bg-muted/60 p-3">
                <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">{step.label}</p>
                <p className="text-sm leading-relaxed">{readable(step.message)}</p>
              </div>
            ))}
          </div>
          <Button
            onClick={() => previewing && setLocation(`/app/flows/templates/${previewing.id}/customize${industryQuery}`)}
            data-testid="button-use-from-preview"
          >
            <Copy className="h-4 w-4 mr-2" />
            {t("flowTemplates.useThisTemplate")}
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
